import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useLoader } from "../../utils/LoaderUtils";
import shippingAPI from "../../api/shippingApi";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import SubmitButton from "../../components/partials/global/SubmitButton";
import TableShimmer from "../../components/partials/TableShimmer";
import ShipmentRow from "./ShipmentRow";

function List() {
  var { id } = useParams();
  var path = id !== "all" ? id : "";

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const { showLoader, hideLoader } = useLoader();
  const [loadingId, setLoadingId] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  const query = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || path || "",
      search: searchParams.get("search") || "",
    }),
    [searchParams, path],
  );

  const fetchShipmentLogs = useCallback(async () => {
    setTableLoading(true);

    try {
      const res = await shippingAPI.list(query);

      if (res.code === 200) {
        setItems(res.data.shipments);
        setPagination(res.data.pagination);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchShipmentLogs();
  }, [query]);

  const updateFilter = useCallback((key, value) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    });
  }, []);

  const startIndex = useMemo(() => {
    return (query.page - 1) * query.limit;
  }, [query]);

  const checkStatus = useCallback(async (shipmentId) => {
    setLoadingId(shipmentId);
    try {
      const res = await shippingAPI.trackShipment(shipmentId);

      if (res.code === 200) {
        const tracking = res.data?.tracking;

        setItems((prev) =>
          prev.map((item) =>
            item._id === shipmentId
              ? {
                  ...item,
                  status: tracking?.status || item.status,
                }
              : item,
          ),
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingId(null);
    }
  }, []);

  return (
    <div className="content">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>All Shipment Details</h4>
            <h6>Keep track and manage your shipment database efficiently</h6>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text" id="basic-addon1">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search By Email Address/Phone Number"
                  value={searchParams.get("search") || ""}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6" style={{ justifyItems: "right" }}>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                <select
                  className="form-control ms-2"
                  value={searchParams.get("status") || ""}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="row">
            <div className="col-md-12">
              <div className="countablepagination p-2">
                <p>
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  of {pagination.total} results
                </p>
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead className="thead-light">
                <tr>
                  <th>S. No.</th>
                  <th>AWB</th>
                  <th>Channel Partner</th>
                  <th>Shipment Details</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <TableShimmer rows={10} cols={8} />
                ) : items.length > 0 ? (
                  items.map((item, index) => (
                    <ShipmentRow
                      key={item._id}
                      item={item}
                      index={index}
                      startIndex={startIndex}
                      loadingId={loadingId}
                      checkStatus={checkStatus}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center p-4 text-muted">
                      No shipment found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="row">
            <RowsPerPage
              value={searchParams.get("limit") || "10"}
              onChange={(val) => updateFilter("limit", val)}
            />

            <Pagination pagination={pagination} updateFilter={updateFilter} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
